"use client";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

export interface ClerkUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  imageUrl: string;
}

interface UsersContextType {
  users: ClerkUser[];
  loading: boolean;
  error: string | null;
  getUserById: (id: string) => ClerkUser | undefined;
  getUserDisplayName: (id: string) => string;
  refreshUsers: () => Promise<void>;
}

const UsersContext = createContext<UsersContextType | null>(null);

// Cache users data at module level with a much longer duration
let cachedUsers: ClerkUser[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export function UsersProvider({ children }: { children: ReactNode }) {
  const [users, setUsers] = useState<ClerkUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      // Check if we have valid cached data
      if (
        cachedUsers &&
        cacheTimestamp &&
        Date.now() - cacheTimestamp < CACHE_DURATION
      ) {
        setUsers(cachedUsers);
        return;
      }

      setLoading(true);
      setError(null);
      const response = await fetch("/api/users");
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();

      // Update cache
      cachedUsers = data;
      cacheTimestamp = Date.now();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []); // Empty dependency array since we manage cache manually

  const getUserById = (id: string) => {
    return users.find((user) => user.id === id);
  };

  const getUserDisplayName = (id: string) => {
    const user = getUserById(id);
    if (!user) return "Unknown User";
    return user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email || "Unknown User";
  };

  const refreshUsers = async () => {
    cachedUsers = null;
    cacheTimestamp = null;
    await fetchUsers();
  };

  return (
    <UsersContext.Provider
      value={{
        users,
        loading,
        error,
        getUserById,
        getUserDisplayName,
        refreshUsers,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
}

export function useUsers() {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error("useUsers must be used within a UsersProvider");
  }
  return context;
}
